import { Injectable } from "@angular/core";
import { WebshopColorTokensDataModel, WebshopColorTokenRoles, WebshopColorTokenBinding, WEBSHOP_COLOR_TOKEN_ROLE_CSS_NAMES, WEBSHOP_COLOR_TOKEN_NAMES } from "../shared/models/webshopColorTokens.model";
import { CoreLogService } from "./log.service";
import { AccessibilityWebShopService } from "./accessibilityWebShop.service";
import { CoreLanguageService } from "./language.service";

/** the kebab-case CSS custom property names of the 10 roles, derived from the single source-of-truth mapping - used to validate `bindings[].role` (Befund 4 / ColorTokenDefaults.vb contract) */
const KNOWN_ROLE_CSS_NAMES: Set<string> = new Set(Object.keys(WEBSHOP_COLOR_TOKEN_ROLE_CSS_NAMES).map((roleKey) => WEBSHOP_COLOR_TOKEN_ROLE_CSS_NAMES[roleKey]));

/** the 70 canonical ZUORDNUNG token names, mirrored from `ColorTokenDefaults.vb` - used to validate `bindings[].token` (property-name position) as an exact allowlist, not a character filter */
const KNOWN_TOKEN_NAMES: Set<string> = new Set(WEBSHOP_COLOR_TOKEN_NAMES);

/**
 * kebab-case CSS name -> camelCase role key, inverted from the same single source-of-truth mapping.
 * The portal's live-preview link names the roles by their CSS name (`brand-contrast`), while
 * `WebshopColorTokenRoles` is keyed camelCase (`brandContrast`) - see `initColorPreviewLive` in
 * `Webshop.coffee`, which does the same conversion in the other direction.
 */
const ROLE_KEY_BY_CSS_NAME: { [cssName: string]: string } = Object.keys(WEBSHOP_COLOR_TOKEN_ROLE_CSS_NAMES)
  .reduce((acc, roleKey) => {
    acc[WEBSHOP_COLOR_TOKEN_ROLE_CSS_NAMES[roleKey]] = roleKey;
    return acc;
  }, {} as { [cssName: string]: string });

/**
 * Writes the customer's portal-managed color tokens into the empty `<style name="webshop-color-tokens">`
 * element (src/index.html) as runtime CSS Custom Property overrides layered on top of the build-time
 * SCSS floor (`custom/.../_custom-color-variable.scss`, untouched). Pure DOM-write responsibility only -
 * `WebShopService` owns the `ObjectObserver`/sync lifecycle and calls `writeColorTokens` on every change,
 * analog to `WebshopSettings` (no per-location ObjectObserver trick needed here, unlike `SOTStyleService`,
 * because one app installation belongs to exactly one customer).
 *
 * Cascade ordering: Angular's build (verified against the compiled `www/index.html` /
 * `www/styles.<hash>.js`) does not emit compiled CSS as a `<link>` in `<head>` - webpack's
 * `style-loader` appends a `<style>` tag to `<head>` at script-execution time, i.e. *after* both
 * static `<style>` tags already present in the shipped `index.html`. Same specificity + later
 * document position means the build defaults would otherwise win over this element. Every write
 * therefore re-appends this element to the end of `<head>` (`writeColorTokens` is idempotent and
 * self-healing: any later reordering is corrected on the next sync-triggered call).
 * A lazy-loaded module DOES exist in this project (`app.routing.ts`: `{ path: "sot", loadChildren:
 * "./sot/sot.module#SOTModule" }`, compiled into `sot-sot-module-ngfactory.*.js`), so a later
 * `style-loader` run after app start is a real, not merely theoretical, possibility. Today it does
 * not re-win the cascade because that module's own stylesheet (`sot/scss/_variables.scss:56-77`)
 * only defines `--acessabilty-color*` custom properties and does not redeclare any of the 10 role
 * properties this service writes - so even if its `<style>` tag lands after this element, there is
 * no overlapping declaration to lose to. This is a property of today's SOT stylesheet content, not
 * a structural guarantee of the lazy-loading mechanism: if a future SOT (or any other lazy-loaded)
 * stylesheet ever redeclares one of the 10 role properties, this class needs re-verification against
 * the compiled output the same way it was verified here. A webpack dev-server HMR re-injection (dev
 * only, never in a Cordova build) would be corrected the same way on the next real sync change.
 *
 * FOUC (by design, not a bug): on cold app start the style element is empty until the first
 * WebshopColorTokens sync completes, so the app briefly renders with build-time colors. An existing
 * user whose customer changed colors in the portal will briefly see the previous colors on the next
 * app start, then the sync overwrites them. On the very first start without network there is no cached
 * sync yet, so the build-time colors remain in effect indefinitely - this is the intended fallback
 * floor, not a defect.
 *
 * Dark/light runtime boundary (documented, not fixed here - out of scope, "Option B" per user decision):
 * `$dark-theme` is a build-time SCSS flag with build-time-only branches a runtime `:root` override cannot
 * reach: core/components/home/home.component.scss:5-6, core/components/mainNav/mainNav.component.scss:1126-1128,
 * core/components/mobile-footer/mobile-footer.component.scss:5-7 and core/shared/scss/_color_variable.scss:135
 * (the full a11y color palette). Toggling `active_theme` at runtime therefore does not repaint those spots
 * until the next app build/release; only the 10 role colors of the currently built theme branch update live.
 */
@Injectable({ providedIn: "root" })
export class WebshopColorTokensService {

  private static readonly STYLE_ELEMENT_NAME = "webshop-color-tokens";
  /**
   * Blocks CSS values that could append a second declaration (semicolon) or reinterpret the current
   * one as a new property (colon), break out of the single `:root` declaration block (curly braces),
   * or close a CSS comment (asterisk-slash) / the surrounding `<style>` element (`</style`). Verified
   * against all 33 literal defaults in `ColorTokenDefaults.vb` (hex colors, `rgba(...)`,
   * `color-mix(in srgb, ...)`) and the migration script's example value shapes
   * (`color-token-migrate.js`) - none of them need a semicolon or colon, so this is not a domain
   * restriction, only an attack-surface reduction.
   */
  private static readonly UNSAFE_VALUE_PATTERN = /[{}:;]|<\/style|\*\//i;

  /** query parameter the portal sets on the live-preview link (`initColorPreviewLive` in `Webshop.coffee`) */
  private static readonly PREVIEW_PARAM = "colorPreview";
  /**
   * Six or eight hex digits WITHOUT a leading `#` - exactly what the portal sends (a `#` in a query
   * string would start the fragment and drop the remaining parameters, so it strips it).
   *
   * Deliberately far stricter than `UNSAFE_VALUE_PATTERN`: values arriving on this path have NOT
   * passed the server-side allowlist in `ColorTokenDefaults.vb` - anyone can build such a link.
   * Because the `#` is prepended by this class and never taken from the URL, every value emitted
   * from a preview is structurally `#` plus 6 or 8 hex digits. CSS injection is therefore
   * impossible here by construction, not merely unlikely.
   */
  private static readonly PREVIEW_HEX_PATTERN = /^[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/;
  private static readonly PREVIEW_BANNER_ID = "webshop-color-preview-banner";
  /**
   * sessionStorage key holding the preview across a page rebuild - see `_restorePreview`.
   *
   * sessionStorage, not localStorage: the entry must die with the tab. A preview is a look at
   * unsaved values; kept in localStorage it would still be showing days later, in every tab, and
   * would be indistinguishable from the customer's real palette.
   */
  private static readonly PREVIEW_STORAGE_KEY = "webshopColorPreview";

  /** camelCase role key -> `#rrggbb(aa)`, only the roles the link actually supplied; null when no preview is active */
  private _previewRoles: { [roleKey: string]: string } = null;
  /**
   * token name (`--color-bg-mixed`) -> `#rrggbb(aa)`, only the tokens the link actually supplied;
   * null when no preview is active.
   *
   * Why this exists next to `_previewRoles`: a token bound to a role follows the preview on its own
   * through `var(--<role>)`, but a token carrying its OWN literal cannot - it is a fixed value in the
   * build-time CSS, and overriding the ten roles never reaches it. Previewing the variant the app was
   * NOT built with therefore used to show that variant's roles on this variant's literals: light page,
   * dark `--color-bg-mixed` card. The portal now resolves every literal of the previewed theme to a
   * plain hex colour and sends it along, so the preview shows the stored values rather than a
   * recomputation - the only form that is also correct for a customer with an own mixed colour.
   *
   * Same structural guarantee as the roles: the value is `#` plus 6 or 8 hex digits by construction
   * (`PREVIEW_HEX_PATTERN`, `#` prepended here), and the property NAME is checked against
   * `KNOWN_TOKEN_NAMES`, so neither position can carry anything but what the allowlist permits.
   */
  private _previewTokens: { [token: string]: string } = null;
  /** the theme the portal says these colours belong to, for the banner text only - see `_readPreviewFromUrl` */
  private _previewTheme = "";

  constructor(private _coreLogService: CoreLogService,
              private _accessibilityWebShopService: AccessibilityWebShopService,
              private _languageService: CoreLanguageService) {
    this._readPreviewFromUrl();
    // Nichts in der Adresse: dann kann dies der Neuaufbau eines Tabs sein, der
    // die Vorschau schon einmal bekommen hatte. Rollen UND Tokens kommen aus
    // derselben Adresse, also entscheidet auch nur eine Pruefung darueber -
    // haette die Adresse Tokens ohne Rollen getragen, wuerde ein Blick allein
    // auf die Rollen den gelesenen Stand wieder mit dem gespeicherten ueberschreiben.
    if (!this._previewRoles && !this._previewTokens) {
      this._restorePreview();
    }
    // Nicht auf writeColorTokens warten. Der Aufruf haengt am Sync der
    // Farbtokens (webShop.service.ts), und den gibt es nicht, solange im
    // Portal noch nie Farben gespeichert wurden - die Vorschau waere dann
    // gelesen, aber nie gezeichnet.
    if (this._writePreviewOnlyBlock(this._getStyleElementMovedToEndOfHead())) {
      // Wie nach jedem Schreiben in writeColorTokens: angeheftete
      // --color-*-Tokens sind FESTE Werte und lesen die Rollen nicht mehr per
      // var(), die Vorschau bliebe fuer sie wirkungslos.
      this._releaseAccessibilityPinning();
    }
  }

  /**
   * Reads the portal's live-preview link once at startup: `?colorPreview=1&theme=dark&brand=8a1426&...`
   *
   * The parameters sit BEFORE the hash on purpose - the shop uses hash routing, and `location.search`
   * reads nothing behind a `#`.
   *
   * Runs ONCE, at construction - and that is why the result is handed to `_rememberPreview`: the
   * address this method reads does not survive the app's own startup (the shop rewrites it, see
   * `routerHelper.service.ts` `setupInitialQueryParamsHandling`), so after any reload there would
   * be nothing left to read.
   *
   * `theme` is carried for the banner text only, never used to decide anything: `$dark-theme` is a
   * build-time SCSS flag (see the class doc above), so the app cannot know which variant it was
   * built with and cannot switch it. Previewing the variant that is not shipped therefore shows the
   * preview colours on the other variant's build-time constants - which is exactly what the portal
   * dialog already warns about ("wirkt in der App erst mit dem naechsten Build").
   */
  private _readPreviewFromUrl(): void {
    const me = this;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get(WebshopColorTokensService.PREVIEW_PARAM) !== "1") {
        return;
      }
      const roles: { [roleKey: string]: string } = {};
      let count = 0;
      Object.keys(ROLE_KEY_BY_CSS_NAME).forEach((cssName) => {
        const raw = params.get(cssName);
        if (raw === null) {
          return;
        }
        if (!WebshopColorTokensService.PREVIEW_HEX_PATTERN.test(raw)) {
          // one bad value must not discard the rest: the role simply keeps its synced colour
          me._coreLogService.error("WebshopColorTokensService", "_readPreviewFromUrl", "rejected preview value for " + cssName + " - not 6 or 8 hex digits");
          return;
        }
        roles[ROLE_KEY_BY_CSS_NAME[cssName]] = "#" + raw;
        count += 1;
      });
      // Die Literal-Tokens des Themes, vom Portal zu Hex aufgeloest. Der
      // Parametername ist der Tokenname OHNE die zwei fuehrenden Bindestriche
      // ("color-bg-mixed"), damit er ohne Kodierung in der Adresse steht; eine
      // Kollision mit den Rollennamen ist ausgeschlossen, weil jeder Tokenname
      // mit "color-" beginnt und keine der zehn Rollen so heisst.
      const tokens: { [token: string]: string } = {};
      WEBSHOP_COLOR_TOKEN_NAMES.forEach((token) => {
        const raw = params.get(token.replace(/^--/, ""));
        if (raw === null) {
          return;
        }
        if (!WebshopColorTokensService.PREVIEW_HEX_PATTERN.test(raw)) {
          // wie bei den Rollen: ein unbrauchbarer Wert kostet nur dieses eine
          // Token, das dann auf seinem gebauten bzw. gesyncten Wert bleibt
          me._coreLogService.error("WebshopColorTokensService", "_readPreviewFromUrl", "rejected preview value for " + token + " - not 6 or 8 hex digits");
          return;
        }
        tokens[token] = "#" + raw;
        count += 1;
      });
      if (count === 0) {
        return;
      }
      if (Object.keys(tokens).length > 0) {
        me._previewTokens = tokens;
      }
      const theme = String(params.get("theme") || "");
      me._previewTheme = (theme === "dark" || theme === "light") ? theme : "";
      // Nur setzen, wenn wirklich eine Rolle dabei war: ein leeres Objekt waere
      // wahrheitswertig und wuerde "es laeuft eine Rollenvorschau" behaupten,
      // obwohl die Adresse nur Tokens trug.
      if (Object.keys(roles).length > 0) {
        me._previewRoles = roles;
      }
      me._rememberPreview();
    } catch (error) {
      // a malformed query string must never keep the shop from starting
      me._coreLogService.error("WebshopColorTokensService", "_readPreviewFromUrl", error);
    }
  }

  /**
   * Writes the preview roles ALONE into the style element - no synced palette involved.
   *
   * Needed because the preview must not depend on the colour-token sync: `writeColorTokens` is only
   * called when `WebShopService` has a token object at all, and its empty-data branch clears the
   * whole block. A customer who has never saved colours in the portal hits exactly that path, on a
   * cycle, which used to wipe a correctly-read preview seconds after it appeared.
   *
   * Roles the link did not supply are deliberately absent rather than emitted empty: they then fall
   * back to the build-time SCSS floor, which is the same state the shop shows before the first sync
   * anyway - a partial preview is honest, an incomplete `:root` block with empty values is broken.
   *
   * Property names come from `WEBSHOP_COLOR_TOKEN_ROLE_CSS_NAMES`, never from the stored keys, for
   * the same allowlist reason as `_buildRoleDeclarations`. The values are already `#` plus 6 or 8
   * hex digits by construction (see `_readPreviewFromUrl` / `_restorePreview`).
   *
   * Written with `!important` and under `:root:root`, unlike the plain `:root` of
   * `writeColorTokens`. That method may rely on document order - it runs long after bootstrap and
   * re-appends the element to the end of `<head>` on every call. This one cannot: it runs DURING
   * bootstrap, Angular injects its own style elements afterwards, and the roles live there under
   * `:root, [data-theme="dark"]` (and `[data-theme="light"]`) - equal specificity to `:root`, so
   * order alone would decide it. `!important` on a custom property is valid and decides which
   * declaration supplies the variable's value; in the cascade an important author declaration
   * outranks everything but another important one - a normal inline style included. Specificity and
   * order are therefore ruled out as causes entirely, and `:root:root` stays as the belt to that
   * braces.
   *
   * Deliberately ONLY here. The regular path in `writeColorTokens` keeps normal declarations: its
   * ordering is guaranteed, and a saved customer colour must stay overridable - the accessibility
   * layer overrides it inline on purpose.
   *
   * @returns true when a preview was written - the caller then must not clear the element.
   */
  private _writePreviewOnlyBlock(styleElement: HTMLStyleElement): boolean {
    const me = this;
    if (!styleElement || (!me._previewRoles && !me._previewTokens)) {
      return false;
    }
    const declarations: string[] = [];
    Object.keys(WEBSHOP_COLOR_TOKEN_ROLE_CSS_NAMES).forEach((roleKey) => {
      const value = me._previewRoles ? me._previewRoles[roleKey] : null;
      if (!value) {
        return;
      }
      // !important: siehe die Begruendung im Methodenkopf. Der Wert ist hier
      // schon strukturell "#" plus 6 oder 8 Hexstellen, es kann also nichts
      // anderes vor dem Schluesselwort landen.
      declarations.push("  --" + WEBSHOP_COLOR_TOKEN_ROLE_CSS_NAMES[roleKey] + ": " + value + " !important;");
    });
    // NACH den Rollen: ein Literal-Token ist die speziellere Aussage - traegt
    // die Adresse fuer dasselbe Token sowohl die Rolle als auch den Eigenwert,
    // gewinnt der Eigenwert, genau wie im gebauten Shop.
    me._appendPreviewTokenDeclarations(declarations, true);
    // Das Theme-Bit der Vorschau: ohne das bliebe die Fusszeile auf der
    // Konstante des gebauten Zweiges stehen - siehe _appendThemeContrastDeclarations.
    me._appendThemeContrastDeclarations(declarations, me._previewTheme, true);
    if (declarations.length === 0) {
      return false;
    }
    styleElement.textContent = ":root:root {\r\n" + declarations.join("\r\n") + "\r\n}\r\n";
    me._showPreviewBanner();
    return true;
  }

  /**
   * Appends the preview's literal tokens to a declaration list.
   *
   * Two callers, two shapes, and `Important` tells them apart because they correspond exactly:
   * `_writePreviewOnlyBlock` writes a `:root:root` block of important declarations (indented, for the
   * reasons in its own doc), `writeColorTokens` appends to the synced `:root` block, where the
   * preview must win over the binding of the SAME token that `_appendBindingDeclarations` just
   * pushed - at equal specificity the later declaration does that on its own, so no `!important`
   * there and the accessibility layer keeps its inline override.
   *
   * The property name is re-checked against `KNOWN_TOKEN_NAMES` although `_readPreviewFromUrl` and
   * `_restorePreview` only ever store allowlisted names: the values pass through sessionStorage,
   * which any script of the same origin can write, so the check belongs where the string is
   * interpolated into CSS, not only where it is read.
   */
  private _appendPreviewTokenDeclarations(declarations: string[], important: boolean): void {
    const me = this;
    if (!me._previewTokens) {
      return;
    }
    Object.keys(me._previewTokens).forEach((token) => {
      if (!KNOWN_TOKEN_NAMES.has(token)) {
        me._coreLogService.error("WebshopColorTokensService", "_appendPreviewTokenDeclarations", "rejected preview token - unknown token name " + token);
        return;
      }
      const value = me._previewTokens[token];
      if (!value) {
        return;
      }
      declarations.push((important ? "  " : "") + token + ": " + value + (important ? " !important;" : ";"));
    });
  }

  /**
   * Publishes the active theme's contrast constant as `--ya-theme-contrast` (and its inverse).
   *
   * Why this is needed: a handful of component stylesheets mix a fixed black or white INTO a role
   * colour to get a slightly set-off surface - the footer bar is the visible one
   * (`main-footer.component.scss`, `mainNav.component.scss`: `color-mix(in srgb,
   * $ya-primary-background-color, var(--footer-theme-color-mix10))`). Which of the two constants
   * applies was decided by `$dark-theme` at BUILD time, and until the colour-token sync existed that
   * was airtight: the palette came from the same build, so branch and palette could never disagree.
   * Since the portal can switch the active theme at runtime they can: a dark build serving light
   * roles mixes white into a white page and the footer turns white instead of grey.
   *
   * The missing piece is exactly one bit - which constant - and both paths already carry it
   * (`active_theme` from the sync, `theme=` from the preview link). The stylesheets read it as
   * `var(--ya-theme-contrast, <the build-time value>)`, so:
   *
   * - nothing declared (no sync yet, no preview) -> the fallback applies -> byte-identical to before
   * - active theme == built theme -> this writes exactly what the fallback holds -> same colour
   * - active theme != built theme -> the only case that changes, and the only case that renders
   *   wrong today
   *
   * The shop cannot know which branch it was built from (`$dark-theme` leaves no runtime trace), and
   * it does not need to: in the matching case the written value and the fallback are the same string.
   */
  private _appendThemeContrastDeclarations(declarations: string[], theme: string, important: boolean): void {
    if (theme !== "dark" && theme !== "light") {
      return;
    }
    const contrast = theme === "dark" ? "#ffffff" : "#000000";
    const inverse = theme === "dark" ? "#000000" : "#ffffff";
    const suffix = important ? " !important;" : ";";
    const indent = important ? "  " : "";
    declarations.push(indent + "--ya-theme-contrast: " + contrast + suffix);
    declarations.push(indent + "--ya-theme-contrast-inv: " + inverse + suffix);
  }

  /**
   * Keeps the preview for this tab so a page rebuild does not lose it.
   *
   * Failure is silent and harmless: without the entry the preview simply ends at the next reload,
   * which is the behaviour this method exists to improve on. Private mode, a full quota or a
   * browser configured to block site data must never keep the shop from starting.
   */
  private _rememberPreview(): void {
    const me = this;
    try {
      window.sessionStorage.setItem(WebshopColorTokensService.PREVIEW_STORAGE_KEY, JSON.stringify({
        theme: me._previewTheme,
        roles: me._previewRoles,
        tokens: me._previewTokens,
      }));
    } catch (error) {
      me._coreLogService.error("WebshopColorTokensService", "_rememberPreview", error);
    }
  }

  /**
   * Reads back what `_rememberPreview` stored, for the case where the address no longer carries the
   * parameters - a reload, or the startup rewriting the URL.
   *
   * Every value is validated AGAIN, against the same `PREVIEW_HEX_PATTERN`, and every key must be a
   * known role key. sessionStorage is not a trusted store: any script of the same origin can write
   * into it, so what comes back out is exactly as untrusted as the query string it came from. The
   * `#` is prepended here and never taken from the stored text, so the same structural guarantee
   * holds as on the URL path - a stored value can only ever become `#` plus 6 or 8 hex digits.
   */
  private _restorePreview(): void {
    const me = this;
    try {
      const raw = window.sessionStorage.getItem(WebshopColorTokensService.PREVIEW_STORAGE_KEY);
      if (!raw) {
        return;
      }
      const stored = JSON.parse(raw);
      if (!stored || (!stored.roles && !stored.tokens)) {
        return;
      }
      const roles: { [roleKey: string]: string } = {};
      let count = 0;
      Object.keys(WEBSHOP_COLOR_TOKEN_ROLE_CSS_NAMES).forEach((roleKey) => {
        const value = String((stored.roles || {})[roleKey] || "");
        if (!WebshopColorTokensService.PREVIEW_HEX_PATTERN.test(value.replace(/^#/, ""))) {
          return;
        }
        roles[roleKey] = "#" + value.replace(/^#/, "");
        count += 1;
      });
      // Dieselbe Pruefung wie auf dem Adressweg, aus demselben Grund: was hier
      // herauskommt, ist genauso ungeprueft wie eine Adresszeile - der Name muss
      // aus der Allowlist stammen, der Wert 6 oder 8 Hexstellen sein, und das "#"
      // setzt diese Klasse, nie der gespeicherte Text.
      const tokens: { [token: string]: string } = {};
      WEBSHOP_COLOR_TOKEN_NAMES.forEach((token) => {
        const value = String((stored.tokens || {})[token] || "");
        if (!WebshopColorTokensService.PREVIEW_HEX_PATTERN.test(value.replace(/^#/, ""))) {
          return;
        }
        tokens[token] = "#" + value.replace(/^#/, "");
        count += 1;
      });
      if (count === 0) {
        // nothing usable in there - drop it rather than reading the same rubbish on every start
        window.sessionStorage.removeItem(WebshopColorTokensService.PREVIEW_STORAGE_KEY);
        return;
      }
      const theme = String(stored.theme || "");
      me._previewTheme = (theme === "dark" || theme === "light") ? theme : "";
      if (Object.keys(roles).length > 0) {
        me._previewRoles = roles;
      }
      if (Object.keys(tokens).length > 0) {
        me._previewTokens = tokens;
      }
    } catch (error) {
      me._coreLogService.error("WebshopColorTokensService", "_restorePreview", error);
    }
  }

  /**
   * Lays the preview colours over the synced palette. Only the roles the link supplied are
   * replaced; the rest keep their saved value, so a link carrying a single role stays meaningful.
   *
   * Bindings are deliberately left untouched: a token following a role picks the preview colour up
   * through `var(--<role>)` on its own, and a token carrying its own literal keeps it - which is
   * correct, because the portal only ever sends roles.
   */
  private _applyPreviewOverride(roles: WebshopColorTokenRoles): WebshopColorTokenRoles {
    const me = this;
    if (!me._previewRoles) {
      return roles;
    }
    const merged: { [roleKey: string]: string } = {};
    Object.keys(WEBSHOP_COLOR_TOKEN_ROLE_CSS_NAMES).forEach((roleKey) => {
      merged[roleKey] = (roles as any)[roleKey];
    });
    Object.keys(me._previewRoles).forEach((roleKey) => {
      merged[roleKey] = me._previewRoles[roleKey];
    });
    return merged as any;
  }

  /**
   * Marks the page as a preview, once. Recommended by the portal contract because the link is
   * forgeable: without the strip, a forwarded link or screenshot is indistinguishable from the
   * real shop.
   *
   * Sits in normal flow before `<app-root>` rather than `position: fixed`, so it pushes the page
   * down instead of covering the header - the header is one of the surfaces being judged. Angular
   * only ever replaces the contents of `<app-root>`, so the strip survives every navigation.
   * Styles are inline: this element lives outside the app's stylesheets, and the colours must stay
   * legible no matter which palette the preview happens to be showing.
   */
  private _showPreviewBanner(): void {
    const me = this;
    if ((!me._previewRoles && !me._previewTokens) || document.getElementById(WebshopColorTokensService.PREVIEW_BANNER_ID)) {
      return;
    }
    const banner = document.createElement("div");
    banner.id = WebshopColorTokensService.PREVIEW_BANNER_ID;
    banner.setAttribute("role", "status");
    banner.setAttribute("style", [
      "box-sizing:border-box", "width:100%", "padding:7px 12px",
      "background:#8a5a00", "color:#ffffff",
      "font:600 13px/1.35 Arial,Helvetica,sans-serif", "text-align:center",
      "letter-spacing:.02em",
    ].join(";"));
    const label = me._languageService.value("phWEBSHOP_ColorPreviewBanner") || "";
    banner.textContent = label.replace("{0}", me._previewTheme);
    const host = document.querySelector("app-root");
    if (host && host.parentNode) {
      host.parentNode.insertBefore(banner, host);
    } else {
      document.body.insertBefore(banner, document.body.firstChild);
    }
  }

  /**
   * Builds one `:root { ... }` string from the 10 active-theme roles plus any customer binding
   * overrides and writes it in a single DOM operation (never per-property `setProperty` calls).
   * Uses `textContent` (not `innerHTML`) so a value can never break out of the `<style>` element
   * into surrounding markup and get executed as script: browsers never execute a `<script>` tag
   * inserted via `innerHTML` either, but jQuery's `.html()` (used by `SOTStyleService`) actively
   * `globalEval`s any `<script>` tag it finds in the string it inserts - `textContent` sidesteps
   * that jQuery-specific behavior entirely, on top of never parsing the value as markup at all.
   * The server-side allowlist (`ColorTokenDefaults.vb`, `LiteralValueAllowedPunctuation`) is the
   * primary defense against malicious CSS. Client-side, every CSS *property name* this method can
   * emit (the 10 roles, and `bindings[].token`/`.role`) is checked against an exact
   * allowlist mirrored from the server contract (`WEBSHOP_COLOR_TOKEN_ROLE_CSS_NAMES`,
   * `WEBSHOP_COLOR_TOKEN_NAMES`) - an allowlist, not a character filter, because a single `;` is
   * enough to close the current declaration and append an attacker-chosen one, and a single `:`
   * is enough to reinterpret an already-open declaration as a different property, neither of which
   * requires "breaking out" of the enclosing `:root { }` block. CSS *values* (10 role values,
   * `bindings[].value` in literal mode) go through `_isSafeCssValue` instead of a second
   * allowlist, since the domain of legitimate values (hex colors, `rgba()`/`color-mix()` etc.) is
   * open-ended, not a small closed set like the property names - `_isSafeCssValue` rejects `;` and
   * `:` (declaration-append/reinterpretation, same reasoning as above, verified against all 33
   * literal defaults in `ColorTokenDefaults.vb` to not need either character), `{`/`}` (rule
   * injection), and comment-close/`</style` (tag closing). This is a light defense-in-depth guard on
   * top of the server allowlist, not a duplicate of it - it does not police which CSS functions are
   * allowed, only the characters that could change what gets declared or where.
   */
  public writeColorTokens(data: WebshopColorTokensDataModel): void {
    const me = this;
    const styleElement = me._getStyleElementMovedToEndOfHead();
    if (!styleElement) {
      return;
    }
    if (!data || !data.roles) {
      // Eine laufende Vorschau darf hier NICHT verschwinden. Dieser Zweig ist
      // beim Kunden ohne gespeicherte Farben der Regelfall, und der Sync ruft
      // ihn zyklisch - geleert hat er die Vorschau nach wenigen Sekunden
      // wieder weggeputzt, obwohl sie korrekt aus der Adresse gelesen war.
      if (!me._writePreviewOnlyBlock(styleElement)) {
        styleElement.textContent = "";
      }
      me._releaseAccessibilityPinning();
      return;
    }
    // Bei jedem Schreiben neu ueberlagern: der Sync laeuft zyklisch weiter und
    // wuerde die Vorschau sonst nach wenigen Minuten still zuruecksetzen.
    const roleDeclarations = me._buildRoleDeclarations(me._applyPreviewOverride(data.roles));
    if (roleDeclarations === null) {
      // the role palette is atomic (Befund 5): a rejected role would otherwise mix new and stale
      // build-default colors into a visually broken, hard-to-diagnose palette - keep the previous
      // style block untouched instead. _buildRoleDeclarations already logged the reason.
      return;
    }
    const declarations = roleDeclarations.slice();
    me._appendBindingDeclarations(data.bindings, declarations);
    // NACH den Bindungen: der Sync liefert die Bindungen des AKTIVEN Themes,
    // die Vorschau zeigt moeglicherweise das andere. Ein Literal-Token traegt
    // dann hier den Wert des falschen Zweiges, und nur die spaetere Deklaration
    // derselben Eigenschaft holt es auf den vorgeschauten Stand zurueck.
    me._appendPreviewTokenDeclarations(declarations, false);
    // Das Theme des Syncs, waehrend einer Vorschau deren Theme: dieselbe
    // Rangfolge wie bei den Rollen und Tokens darueber.
    me._appendThemeContrastDeclarations(declarations, me._previewTheme || String(data.active_theme || ""), false);
    styleElement.textContent = ":root {\r\n" + declarations.join("\r\n") + "\r\n}\r\n";
    me._showPreviewBanner();
    me._releaseAccessibilityPinning();
  }

  /**
   * AccessibilityWebShopService.applyDynamicColors schreibt die Farben als Inline-Style an das
   * Wurzelelement (root.style.setProperty). Inline schlaegt jedes Stylesheet - also auch den
   * <style>-Block, den diese Klasse gerade geschrieben hat. Wurde der Accessibility-Dialog in
   * dieser Sitzung schon einmal angewandt (auch das Zuruecksetzen auf "alles aus" zaehlt), waeren
   * ohne diesen Aufruf alle Tokens aus dessen COLOR_VARS auf dem damaligen Stand eingefroren und
   * die Portal-Einstellung fuer sie wirkungslos.
   *
   * Muss NACH dem Schreiben von textContent laufen: refreshBaseColors liest die neuen Originale
   * ueber getComputedStyle, und das liefert erst dann die soeben geschriebenen Werte. Der Aufruf
   * ist ein No-op, solange nie inline gepinnt wurde - der Regelfall, in dem der Dialog nie
   * geoeffnet wurde, kostet also nichts.
   */
  private _releaseAccessibilityPinning(): void {
    this._accessibilityWebShopService.refreshBaseColors();
  }

  /** fetches (or leaves as-is if missing) the shipped style element and moves it to the end of <head> so it always wins the cascade over the build's own styles bundle, see class doc "Cascade ordering" */
  private _getStyleElementMovedToEndOfHead(): HTMLStyleElement {
    const styleElement = document.querySelector<HTMLStyleElement>(`style[name="${WebshopColorTokensService.STYLE_ELEMENT_NAME}"]`);
    if (styleElement) {
      document.head.appendChild(styleElement);
    }
    return styleElement;
  }

  /**
   * All-or-nothing (Befund 5): only ever emits the 10 known CSS names from
   * `WEBSHOP_COLOR_TOKEN_ROLE_CSS_NAMES`, never a raw key taken from `roles` itself - this closes
   * Befund 3 structurally (there is no code path that could interpolate an unvalidated role key
   * into the declaration) rather than by an extra character check.
   */
  private _buildRoleDeclarations(roles: WebshopColorTokenRoles): string[] {
    const me = this;
    const declarations: string[] = [];
    const rolesLookup: { [roleKey: string]: string } = roles as any;
    for (const roleKey of Object.keys(WEBSHOP_COLOR_TOKEN_ROLE_CSS_NAMES)) {
      const cssName = WEBSHOP_COLOR_TOKEN_ROLE_CSS_NAMES[roleKey];
      const value = rolesLookup ? rolesLookup[roleKey] : undefined;
      if (!me._isSafeCssValue(value)) {
        me._coreLogService.error("WebshopColorTokensService", "_buildRoleDeclarations", "rejected role palette - unsafe or missing value for " + roleKey + ", keeping previous style block");
        return null;
      }
      declarations.push("--" + cssName + ": " + value + ";");
    }
    return declarations;
  }

  /**
   * All 70 ZUORDNUNG tokens of the active theme, never a partial set: the build-time SCSS floor is
   * per-customer, so a missing entry would leave that token on whatever the customer's own SCSS
   * happens to say instead of on a shared default (there is none - see WebshopColorTokenBinding).
   * Each entry is independent, so a rejected one is simply skipped rather than failing the batch;
   * that token then keeps its build-time value, which is the same outcome as before this sync.
   */
  private _appendBindingDeclarations(bindings: WebshopColorTokenBinding[], declarations: string[]): void {
    const me = this;
    (bindings || []).forEach((override) => {
      if (!override || !override.token) {
        return;
      }
      // property-name position: exact allowlist, not a character filter - a single ";" is enough to append
      // an attacker-chosen declaration without ever needing to break out of the enclosing :root { } block
      if (!KNOWN_TOKEN_NAMES.has(override.token)) {
        me._coreLogService.error("WebshopColorTokensService", "_appendBindingDeclarations", "rejected binding - unknown token name " + override.token);
        return;
      }
      let value: string;
      if (override.mode === "role") {
        // contract-mandated (ColorTokenDefaults.vb): only interpolate a role name after validating it against the 10 known roles (Befund 4)
        if (!KNOWN_ROLE_CSS_NAMES.has(override.role)) {
          me._coreLogService.error("WebshopColorTokensService", "_appendBindingDeclarations", "rejected binding for token " + override.token + " - unknown role name " + override.role);
          return;
        }
        value = "var(--" + override.role + ")";
      } else {
        value = override.value;
      }
      if (me._isSafeCssValue(value)) {
        declarations.push(override.token + ": " + value + ";");
      } else {
        me._coreLogService.error("WebshopColorTokensService", "_appendBindingDeclarations", "rejected unsafe binding value for " + override.token);
      }
    });
  }

  /** rejects CSS values that could inject a new rule or close a comment/style block; anything else is the server allowlist's job */
  private _isSafeCssValue(value: string): boolean {
    return typeof value === "string" && value.length > 0 && !WebshopColorTokensService.UNSAFE_VALUE_PATTERN.test(value);
  }
}
